import { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiSearch } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import storageService from '../../services/storageService';

/**
 * Componente para gestionar notas de estudio bíblico
 * - Usuario registrado: guarda localmente (IndexedDB)
 * - Lector con permisos: puede sincronizar con backend
 */
export default function BibleNotes() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    reference: '',
    title: '',
    content: '',
    tags: ''
  });

  useEffect(() => {
    if (user) {
      loadNotes();
    }
  }, [user]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      await storageService.init();
      // Filtrar por usuario actual
      const allNotes = user?.id 
        ? await storageService.bibleNotes.obtenerPorUsuario(user.id)
        : await storageService.bibleNotes.obtenerTodas();
      console.log('📝 Notas bíblicas cargadas:', allNotes.length);
      setNotes(allNotes);
    } catch (error) {
      console.error('Error cargando notas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.reference || !formData.content) {
      alert('Referencia y contenido son obligatorios');
      return;
    }

    try {
      const noteData = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        userId: user?.id,
        createdAt: editingId ? undefined : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (editingId) {
        await storageService.bibleNotes.actualizar(editingId, noteData);
        console.log('✅ Nota actualizada');
      } else {
        await storageService.bibleNotes.agregar(noteData);
        console.log('✅ Nota creada');
      }

      setFormData({ reference: '', title: '', content: '', tags: '' });
      setEditingId(null);
      setShowForm(false);
      await loadNotes();
    } catch (error) {
      console.error('Error guardando nota:', error);
      alert('Error al guardar la nota');
    }
  };

  const handleEdit = (note) => {
    setFormData({
      reference: note.reference,
      title: note.title || '',
      content: note.content,
      tags: (note.tags || []).join(', ')
    });
    setEditingId(note.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta nota?')) return;

    try {
      await storageService.bibleNotes.eliminar(id);
      console.log('✅ Nota eliminada');
      await loadNotes();
    } catch (error) {
      console.error('Error eliminando nota:', error);
      alert('Error al eliminar la nota');
    }
  };

  const filteredNotes = notes.filter(note => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      note.reference?.toLowerCase().includes(search) ||
      note.title?.toLowerCase().includes(search) ||
      note.content?.toLowerCase().includes(search) ||
      note.tags?.some(tag => tag.toLowerCase().includes(search))
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">📝 Mis Notas de Estudio</h2>
          <p className="text-sm text-gray-600 mt-1">
            {notes.length} {notes.length === 1 ? 'nota' : 'notas'} guardadas localmente
          </p>
        </div>
        <button
          onClick={() => {
            setFormData({ reference: '', title: '', content: '', tags: '' });
            setEditingId(null);
            setShowForm(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <FiPlus className="w-5 h-5" />
          Nueva Nota
        </button>
      </div>

      {/* Formulario */}
      {showForm && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingId ? '✏️ Editar Nota' : '➕ Nueva Nota'}
            </h3>
            <button
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setFormData({ reference: '', title: '', content: '', tags: '' });
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Referencia Bíblica *
                </label>
                <input
                  type="text"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  placeholder="Ej: Juan 3:16-18"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título (opcional)
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Título de la nota"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contenido *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Escribe tu nota de estudio..."
                required
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Etiquetas (separadas por comas)
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="teología, profecía, doctrina..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData({ reference: '', title: '', content: '', tags: '' });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <FiSave className="w-4 h-4" />
                {editingId ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Barra de búsqueda */}
      {notes.length > 0 && (
        <div className="mb-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por referencia, título, contenido o etiquetas..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {/* Lista de notas */}
      {filteredNotes.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {notes.length === 0 ? (
            <>
              <p className="text-lg mb-2">📭 No tienes notas</p>
              <p className="text-sm">Crea tu primera nota haciendo clic en "Nueva Nota"</p>
            </>
          ) : (
            <>
              <p className="text-lg mb-2">🔍 No se encontraron notas</p>
              <p className="text-sm">Intenta con otros términos de búsqueda</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredNotes.map((note) => (
            <div
              key={note.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      {note.reference}
                    </span>
                    {note.title && (
                      <h3 className="text-lg font-bold text-gray-900">{note.title}</h3>
                    )}
                  </div>
                  {note.tags && note.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {note.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(note)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Editar"
                  >
                    <FiEdit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(note.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Eliminar"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div 
                className="text-gray-700 prose max-w-none"
                dangerouslySetInnerHTML={{ __html: note.content }}
              />

              {note.updatedAt && (
                <div className="text-xs text-gray-500 mt-2">
                  Última actualización: {new Date(note.updatedAt).toLocaleString('es-ES')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Nota informativa */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>💡 Tip:</strong> Las notas se guardan localmente en tu navegador. Si quieres guardarlas en el servidor, 
          necesitas permisos de "Lector" en el módulo de Biblia.
        </p>
      </div>
    </div>
  );
}


